import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { SEO } from '../components/SEO';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const whoWeWorkWithPoints = [
  'You want a clear plan before making decisions',
  'You value trusted recommendations and strong communication',
  'You want to avoid unnecessary stress and costly mistakes',
  'You appreciate a structured, elevated approach'
];

export const About = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="about-page">
      <SEO
        title="About White Glove Event Concierge | 20+ Years Experience"
        description="Meet the Nashville event advisor behind White Glove: 20+ years in venue operations, vendor alignment, and event execution."
      />
      {/* HERO SECTION */}
      <section className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">About</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mt-4 mb-8">
              Built on experience.{' '}
              <span className="italic text-[#D4AF37]">Designed with intention.</span>
            </h1>
            <p className="text-white/70 text-lg leading-relaxed max-w-2xl mx-auto">
              White Glove Event Concierge was created to bring clarity, structure, and elevated support to the event process—guiding clients through decisions with confidence and precision.
            </p>
          </motion.div>
        </div>
      </section>

      {/* INTRO SECTION - Left Aligned */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">
              A more strategic approach to events
            </h2>
            <div className="space-y-6 text-white/70 text-lg leading-relaxed">
              <p>
                Not every client needs a full-service planner. What many need is something more refined—an experienced professional who understands how every element of an event comes together and can guide the process with clarity and intention.
              </p>
              <p>
                White Glove Event Concierge was built to fill that gap. We operate as a strategic partner, helping clients make informed decisions, align the right vendors, and move forward with confidence from the very beginning.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOUNDER STORY SECTION - With Image */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeInUp}>
              <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">Our Story</span>
              <h2 className="font-heading text-3xl md:text-4xl text-white mt-4 mb-8">
                Founded on real-world experience
              </h2>
              <div className="space-y-6 text-white/70 text-lg leading-relaxed">
                <p>
                  With over 20 years of experience across event operations, venue leadership, and client experience, White Glove Event Concierge is rooted in a deep understanding of how events function behind the scenes.
                </p>
                <p>
                  From managing venue operations to refining sales processes and improving event execution at scale, that experience now translates into a more elevated, client-focused approach—one that prioritizes clarity, efficiency, and intentional decision-making at every stage.
                </p>
                <p>
                  This perspective allows us to see the full picture, anticipate challenges before they arise, and guide clients toward outcomes that are both seamless and impactful.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="/assets/about-portrait.png"
                alt="Amanda Hunt consulting with clients - White Glove Event Concierge"
                className="w-full h-[500px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY SECTION - Centered */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">
              Clarity. Alignment. Execution.
            </h2>
            <div className="space-y-6 text-white/70 text-lg leading-relaxed">
              <p>
                Every event is a series of decisions. The difference between a smooth, elevated experience and a stressful one often comes down to how those decisions are made.
              </p>
              <p>
                Our role is to bring structure to the process—ensuring that budgets are realistic, vendors are aligned, and every element works together as intended.
              </p>
              <p className="text-white font-medium">
                We don&apos;t believe in overcomplicating the process. We believe in guiding it with precision.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* POSITIONING SECTION - Gold accent */}
      <section className="py-24 bg-[#D4AF37]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl text-black mb-8">
              Not a planner. Not a vendor.
            </h2>
            <div className="space-y-6 text-black/80 text-lg leading-relaxed">
              <p>
                White Glove Event Concierge operates differently.
              </p>
              <p>
                We are not a full-service planning company, and we are not a vendor. We serve as your advisor, connector, and strategic partner—helping you navigate the process, avoid costly missteps, and elevate the overall experience without unnecessary complexity.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHO WE WORK WITH SECTION - Left Aligned with Points */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
              Who this is for
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              Our clients value professionalism, efficiency, and a higher level of service. Whether planning a corporate gathering, social celebration, or custom event experience, they are looking for clarity, trusted guidance, and a more refined approach.
            </p>
            <ul className="space-y-4">
              {whoWeWorkWithPoints.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span className="text-white/80">{point}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* PERSONAL NOTE SECTION - With Image */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              <img
                src="https://images.unsplash.com/photo-1670529776286-f426fb7ba42c?q=80&w=800&auto=format&fit=crop"
                alt="Event Excellence"
                className="w-full h-[500px] object-cover"
              />
            </motion.div>
            <motion.div {...fadeInUp} className="order-1 lg:order-2">
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">
                A personal approach to every event
              </h2>
              <div className="space-y-6 text-white/70 text-lg leading-relaxed">
                <p>
                  At the core of White Glove Event Concierge is a commitment to doing things with intention.
                </p>
                <p>
                  Every recommendation, every connection, and every decision is guided by experience and a genuine understanding of what it takes to bring an event together seamlessly.
                </p>
                <p className="text-white">
                  This isn&apos;t about adding more to your plate—it&apos;s about refining the process so everything works the way it should.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CLOSING CTA SECTION - Centered */}
      <section className="py-24 bg-[#050505] border-t border-white/10">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
              Start with clarity
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              Every successful event begins with the right foundation. Our process is designed to provide clarity from the very beginning—so you can move forward with confidence and focus on what matters most.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-[#D4AF37] text-black text-sm uppercase tracking-widest hover:bg-[#C9A432] transition-colors"
              data-testid="about-cta-btn"
            >
              Book a Strategy Session <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
